import React, { useEffect, useMemo, useRef, useState } from "react";
import { Download, Wand2, Layers, Info, X, Repeat, Pause, Loader2 } from "lucide-react";
import { toast } from "sonner";
import UploadZone from "@/components/UploadZone";
import { Switch } from "@/components/ui/switch";
import GenerationGrid from "@/components/GenerationGrid";
import GenerateButton, { useCost } from "@/components/GenerateButton";
import PromptBox from "@/components/PromptBox";
import FrameEditor from "@/components/cleanplate/FrameEditor";
import CleanPlateDialog, { Candidates } from "@/components/cleanplate/CleanPlateDialog";
import AlphaGenDialog from "@/components/cleanplate/AlphaGenDialog";
import MaskHistoryPicker from "@/components/cleanplate/MaskHistoryPicker";
import { PartsPanel, platesFor } from "@/components/cleanplate/PartsPanel";
import { maxRes } from "@/lib/useModels";
import { api, errMsg, mediaUrl } from "@/lib/api";
import { useAttachments } from "@/lib/useUploader";
import useCpSession, { tagOf } from "@/lib/useCpSession";
import Step from "@/components/BfxStep";
import InlineAlert from "@/components/InlineAlert";
import ReplaceSource from "@/components/ReplaceSource";
import VirtualTrim from "@/components/VirtualTrim";
import SequenceImport from "@/components/SequenceImport";

export const CP_VIDEO_PREFIX = "Use the source video as it is — same camera motion, same lighting, same video length. Apply the provided clean plates to the video with full consistency: match exact camera motion, lighting, motion blur, and grain. Keep everything outside the clean plate area identical to the source.";
export const CP_CONTINUATION = "Maintain the continuation of generated frames from the allocated cleanup plates. Use the cleanup plate frames and generate the continuation to maintain consistency.";
export const CP_NEGATIVE = "flickering, inconsistent lighting, motion artifacts, texture mismatch, color shift, blurring on untouched areas";

export const ACT_VIDEO_PREFIX = "Use the source video as it is — same camera motion, same character animation and performance, same lighting, same video length. Add the provided action plates as practical VFX elements in the shot with strict continuity: each effect appears at its plate's keyframe and continues naturally frame-to-frame from there, following the source animation exactly. Match exact camera motion, lighting, motion blur and grain. Keep everything outside the effect area identical to the source.";
export const ACT_CONTINUATION = "Maintain the continuation of generated frames from the allocated action plates. Use the action plate frames and generate the continuation to maintain consistency with the source animation.";
const ACT_NEGATIVE = "morphing, cuts, new camera moves, changed character animation, flickering, inconsistent lighting, texture mismatch, color shift, blurring on untouched areas";
const ACT_PLATE_DEFAULT = "the action VFX element shown in this plate";

const VARIANTS = {
  cleanup: { slot: "cp", session: "cleanplate", tool: "bfx-clean-plate", plate: "cleanplate", noun: "clean plate", Noun: "Clean plate", steps: false, hidden: false, optional: false,
    dialog: null, empty: "Upload a source video, mark keyframes, add clean plates and press Generate." },
  action: { slot: "act", session: "action", tool: "bfx-action", plate: "actionplate", noun: "action plate", Noun: "Action plate", steps: true, hidden: true, optional: true,
    dialog: { title: "Create Action Plate", noun: "action plate", optional: true, danger: true, hideTemplate: true },
    empty: "Load footage, mark keyframes, create action plates and press Generate." },
};
const Wrap = ({ on, children, ...p }) => (on ? <Step {...p}>{children}</Step> : <>{children}</>);

const ModeToggle = ({ id, mode, onChange }) => (
  <div className="grid grid-cols-2 gap-1 mb-1.5" data-testid={`act-mode-${id}`}>
    {[["exact", "Exact Match", "Matches the source at this plate's frame, natural build-up and follow-through"], ["reference", "Take as Reference", "Inspiration only — your prompt leads"]].map(([k, l, t]) => (
      <button key={k} type="button" title={t} onClick={() => onChange(k)} data-testid={`act-mode-${k}-${id}`} data-active={mode === k ? "true" : "false"}
        className={`h-7 rounded-md text-[11px] font-semibold border transition-colors ${mode === k ? "bg-orange-500/90 border-orange-400 text-white" : "border-zinc-700 text-zinc-300 hover:border-orange-400/60"}`}>{l}</button>
    ))}
  </div>
);

// Mirrors the server: the whole clip is one generation (no splicing or blending).
const actionGroups = (keys, fps, frames, dmin, dmax) => {
  if (!frames) return { list: [], err: "" };
  return frames / fps > dmax + 0.001
    ? { list: [], err: `BFX Action generates the whole clip in one go — it is ${(frames / fps).toFixed(2)} s, this model takes up to ${dmax} s. Trim the clip or pick a longer model.` }
    : { list: [{ ws: 0, we: frames - 1, keys }], err: "" };
};

const Label = ({ children, hint }) => <div className="text-xs text-zinc-300 mb-1.5">{children}{hint && <span className="text-zinc-500"> {hint}</span>}</div>;
const clean = (r) => { if (!r) return r; const { preview, uploading, progress, tid, ...rest } = r; return preview && !String(preview).startsWith("blob:") ? { ...rest, preview } : rest; };

const KeyframeList = ({ keyframes, onRemove, trim }) => (
  <div data-testid="cp-keyframes">
    <Label hint={`· ${keyframes.length}`}>Keyframes</Label>
    {!keyframes.length ? <p className="text-[11px] text-zinc-500">Doodle on a frame in the viewer and press Save Keyframe.</p> : (
      <div className="space-y-1.5">
        {keyframes.map((k) => (
          <div key={k.frame} className="flex items-center gap-2 p-1.5 rounded-lg border border-zinc-800 bg-[#0B0F16]" data-testid={`cp-keyframe-${k.frame}`}>
            <img src={mediaUrl(k.keyframe.url)} alt="" className="h-9 w-16 object-cover rounded" />
            <span className="flex-1 text-[11px] font-mono text-zinc-300 truncate" data-testid={`cp-keyframe-name-${k.frame}`}>{tagOf(k.keyframe)}</span>
            {trim && (k.frame < trim[0] || k.frame > trim[1])
              ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400" data-testid={`cp-keyframe-off-${k.frame}`} title="Outside the trim — used only as a reference image">off · reference</span>
              : trim ? <span className="text-[10px] font-mono text-[#7AF25F]" data-testid={`cp-keyframe-remap-${k.frame}`}>→ #{k.frame - trim[0]}</span> : null}
            <a href={mediaUrl(k.keyframe.url)} download={`${tagOf(k.keyframe)}.png`} title="Download this frame" data-testid={`cp-keyframe-download-${k.frame}`} className="text-zinc-500 hover:text-white"><Download className="w-4 h-4" /></a>
            <button type="button" onClick={() => onRemove(k.frame)} data-testid={`cp-keyframe-remove-${k.frame}`} className="text-zinc-500 hover:text-red-400"><X className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    )}
  </div>
);

const FillPicker = ({ value, onChange, secs, min }) => (
  <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-2.5" data-testid="cp-fill">
    <div className="text-[11px] text-amber-200 mb-1.5">Clip is {secs.toFixed(2)} s — this model needs at least {min} s. Fill it, the result is trimmed back to your frames:</div>
    <div className="flex gap-1.5">
      {[["loop", "Loop", Repeat], ["freeze", "Freeze last frame", Pause]].map(([id, l, I]) => (
        <button key={id} type="button" onClick={() => onChange(id)} data-testid={`cp-fill-${id}`}
          className={`ax-chip ${value === id ? "!border-[#1e97f3] text-white bg-[#0747a1]/30" : ""}`}><I className="w-3.5 h-3.5 mr-1" />{l}</button>
      ))}
    </div>
  </div>
);

const RES_PX = { "360p": 360, "480p": 480, "540p": 540, "720p": 720, "768p": 768, "1080p": 1080, "1K": 1024, "2K": 1440, "4K": 2160 };
export const matchRes = (res, info) => {
  const short = info?.width && info?.height ? Math.min(info.width, info.height) : 1080;
  const sorted = [...res].filter((r) => RES_PX[r]).sort((a, b) => RES_PX[a] - RES_PX[b]);
  return sorted.find((r) => RES_PX[r] >= short) || sorted[sorted.length - 1] || res[0];
};

const pickAspect = (caps, info) => {
  const a = caps.aspect_ratios || [];
  if (a.includes("adaptive")) return "adaptive";
  if (a.includes("auto")) return "auto";
  return info?.width && info?.height && info.height > info.width ? "9:16" : "16:9";
};

export default function CleanPlateStudio({ projectId, models = [], items, onUploaded, onOpen, onRetry, header, onChanged, generate, tool, brand, toolCard, resetButton, variant = "cleanup" }) {
  const V = VARIANTS[variant];
  const { st, patch, setMask } = useCpSession(projectId, V.session);
  const source = useAttachments(projectId, 1, onUploaded, false, `${V.slot}-source`, 30, (m) => fl("source", m));
  const plates = useAttachments(projectId, 10, onUploaded, true, `${V.slot}-plates`);
  const mask = useAttachments(projectId, 1, onUploaded, false, `${V.slot}-mask`, 30);
  const [info, setInfo] = useState(null);
  const [dlg, setDlg] = useState(false);
  const [alphaDlg, setAlphaDlg] = useState(false);
  const [zipping, setZipping] = useState(false);
  const [busy, setBusy] = useState(false);
  const loaded = useRef(false);
  const [boot, setBoot] = useState(false);
  const serverMask = useRef(null);
  const ready = !!st;
  const src = source.items[0];
  const keyframes = useMemo(() => st?.keyframes || [], [st?.keyframes]);

  useEffect(() => {
    if (!st || loaded.current) return;
    loaded.current = true;
    setBoot(true);
    serverMask.current = st.mask?.id || null;
    if (st.source) source.setItems([clean(st.source)]);
    plates.setItems((st.plates || []).map(clean));
    if (st.mask) mask.setItems([clean(st.mask)]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [st]);

  const srcId = src?.id || null;
  useEffect(() => {
    if (!loaded.current) return;
    if (srcId === (st.source?.id || null)) return;
    if (srcId) patch({ source: clean(src), ...(st.source?.id ? { trim: null } : {}) });
    else if (!source.uploading) patch({ source: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [srcId, boot]);
  useEffect(() => {
    setInfo(null);
    if (srcId) api.get(`/references/${srcId}/info`).then((r) => { setInfo(r.data); patch({ stem: r.data.stem }); }).catch(() => fl("source", "Could not read that video"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [srcId]);

  const plateKey = plates.ids.join(",");
  useEffect(() => {
    if (loaded.current) patch({ plates: plates.items.filter((p) => p.id).map(clean) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plateKey, boot]);

  const bgOnly = !!st?.bg_only;
  const rawMaskId = mask.ids[0] || null;
  const maskId = bgOnly ? null : rawMaskId;
  useEffect(() => {
    if (!loaded.current || rawMaskId === (st.mask?.id || null)) return;
    if (rawMaskId || !st.alpha_job) setMask(rawMaskId ? clean(mask.items[0]) : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawMaskId]);
  useEffect(() => {
    if (!loaded.current || (st?.mask?.id || null) === serverMask.current) return;
    serverMask.current = st?.mask?.id || null;
    if (st?.mask?.id && st.mask.id !== maskId) { mask.setItems([st.mask]); toast.success("AlphaGen mask attached to Luma Mask"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [st?.mask?.id]);
  useEffect(() => { if (st?.alpha_error) fl("models", `AlphaGen: ${st.alpha_error}`); }, [st?.alpha_error]); // eslint-disable-line react-hooks/exhaustive-deps

  const limits = useMemo(() => tool?.video_models || {}, [tool]);
  const vmodels = useMemo(() => Object.keys(limits).map((id) => models.find((m) => m.id === id)).filter(Boolean), [models, limits]);
  const modelId = st?.model_id || "seedance-2.5";
  const model = vmodels.find((m) => m.id === modelId);
  const caps = model?.capabilities || {};
  const res = caps.resolutions || [];
  const resolution = res.includes(st?.resolution) ? st.resolution : matchRes(res, info);
  const minOf = (m) => m?.capabilities?.duration_seconds?.min || 1;
  const dmin = minOf(model), dmax = caps.duration_seconds?.max || 30;
  const trim = info && st?.trim && st.trim[1] <= info.frames - 1 ? st.trim : null;
  const tFrames = trim ? trim[1] - trim[0] + 1 : info?.frames || 0;
  const secs = tFrames && info?.fps ? tFrames / info.fps : 0;
  const short = secs > 0 && secs < dmin - 0.01;
  const fill = st?.fill || null;
  const duration = Math.min(dmax, Math.max(dmin, Math.ceil(secs - 0.001)));
  const vtemplate = V.hidden ? ACT_VIDEO_PREFIX : st?.vtemplate ?? CP_VIDEO_PREFIX;
  const plateEdits = st?.plate_edits || {};
  const vnotes = st?.vnotes || "";
  const plateList = plates.items.filter((p) => p.id);
  const plateLine = (p) => `@${tagOf(p)} — ${(plateEdits[p.id] || "").trim() || (V.optional ? ACT_PLATE_DEFAULT : "")}`;
  const prompt = [vtemplate.trim(), V.hidden ? ACT_CONTINUATION : CP_CONTINUATION, ...plateList.map(plateLine), vnotes.trim(),
    bgOnly ? "BG ONLY (strict): remove every character / subject of the source video from the final result — no person, body part, shadow or silhouette of them. Keep the exact same camera angle and movement, background, lighting and timing; fill where the subject was with the matching background." : ""].filter(Boolean).join("\n");
  const negBase = V.hidden ? ACT_NEGATIVE : st?.negative ?? CP_NEGATIVE;
  const negative = negBase + (bgOnly ? ", any person, character or creature, leftover body parts, ghost silhouette" : "");
  const settings = { duration, resolution, aspect_ratio: pickAspect(caps, info), negative_prompt: negative, bfx_tool: V.tool, ...(trim ? { in_frame: trim[0], out_frame: trim[1] } : {}),
    ...(maskId ? { mask_id: maskId } : {}), ...(short ? { fill, fill_target: dmin } : {}) };
  const usdRaw = useCost(model?.available ? modelId : null, V.steps ? { ...settings, duration: dmax } : settings);
  const missingPlate = V.optional ? null : plateList.find((p) => !(plateEdits[p.id] || "").trim());

  const saveKeyframe = (k) => patch((s) => ({ keyframes: [...(s.keyframes || []).filter((x) => x.frame !== k.frame), k].sort((a, b) => a.frame - b.frame) }));
  const removeKeyframe = (f) => patch((s) => ({ keyframes: (s.keyframes || []).filter((x) => x.frame !== f) }));
  const stem = info?.stem || st?.stem || "shot";

  const attachZip = async () => {
    setZipping(true);
    try {
      const r = await api.post("/bfx/cleanplate/zip", { reference_id: srcId, keyframes: keyframes.map((k) => ({ frame: k.frame, keyframe_id: k.keyframe.id })) }, { responseType: "blob", timeout: 300000 });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data); a.download = `${stem}_for_cleanup.zip`; a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 30000);
      toast.success("Frames downloaded — make your clean plates and add them below");
    } catch (e) { fl("plates", errMsg(e, "Could not extract those frames")); } finally { setZipping(false); }
  };

  const plateFrame = (p) => { const m = tagOf(p).match(new RegExp(`_(\\d+)_${V.plate}(_v\\d+)?$`)); return m && keyframes.some((k) => k.frame === Number(m[1])) ? Number(m[1]) : null; };
  const assignPlate = async (p, frame) => {
    const taken = new Set(plates.items.filter((x) => x.id !== p.id).map((x) => tagOf(x)));
    let name = `${stem}_${frame}_${V.plate}`;
    for (let v = 2; taken.has(name); v += 1) name = `${stem}_${frame}_${V.plate}_v${v}`;
    try {
      const { data } = await api.patch(`/references/${p.id}`, { name: `${name}.png` });
      plates.setRaw((cur) => cur.map((x) => (x.id === p.id ? { ...x, name: data.name } : x)));
      patch((s) => ({ plates: (s.plates || []).map((x) => (x.id === p.id ? { ...x, name: data.name } : x)) }));
    } catch (e) { fl("plates", errMsg(e, "Could not assign that keyframe")); }
  };
  const unassigned = plateList.find((p) => plateFrame(p) == null);
  useEffect(() => {
    if (!unassigned || unassigned.uploading || !unassigned.id) return;
    const nums = (tagOf(unassigned).match(/\d+/g) || []).map(Number).reverse();
    const f = nums.find((n) => keyframes.some((k) => k.frame === n));
    if (f != null) assignPlate(unassigned, f);
  }, [unassigned?.id, keyframes.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const total = info?.frames || 0;
  const partMax = Math.min(limits[modelId] || dmax, dmax);
  const plateModes = st?.plate_modes || {};
  const farKf = info ? keyframes.find((k) => k.frame > total - 1) : null;
  const fpsV = info?.fps || 24;
  const anchors = (() => {
    if (!V.steps) return [];
    const used = new Set();
    const out = plateList.map((p) => ({ p, f: plateFrame(p) })).filter((x) => x.f != null).map((x) => {
      used.add(x.f);
      return { ref_id: x.p.id, key_frame: x.f, kind: plateModes[x.p.id] || "exact", prompt: (plateEdits[x.p.id] || "").trim() };
    });
    keyframes.filter((k) => !used.has(k.frame)).forEach((k) => {
      const r = k.cleanup || k.keyframe;
      if (r?.id) out.push({ ref_id: r.id, key_frame: k.frame, kind: "guide", prompt: ((st?.kf_edits || {})[k.frame] || "").trim() });
    });
    return out;
  })();
  const groups = actionGroups(anchors.map((x) => x.key_frame), fpsV, tFrames, dmin, partMax);
  const actSecs = groups.list.reduce((acc, g) => acc + Math.max(dmin, Math.ceil((g.we - g.ws + 1) / fpsV - 0.001)), 0);
  const usd = V.steps && usdRaw != null ? (usdRaw / dmax) * actSecs : usdRaw;
  const setMode = (id, m) => patch((s0) => ({ plate_modes: { ...(s0.plate_modes || {}), [id]: m } }));
  const [flash, setFlash] = useState(null);
  const flT = useRef(null);
  const fl = (at, msg) => { setFlash({ at, msg: String(msg) }); clearTimeout(flT.current); flT.current = setTimeout(() => setFlash(null), 9000); };
  const [selPlate, setSelPlate] = useState(null);
  const selP = selPlate ? plates.items.find((x) => x.id === selPlate) : null;
  useEffect(() => {
    if (unassigned && keyframes.length === 1) assignPlate(unassigned, keyframes[0].frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unassigned?.id, keyframes.length]);

  const useIt = async (g, i, job) => {
    try {
      const taken = new Set(plates.items.map((p) => tagOf(p)));
      let name = `${stem}_${job.frame}_${V.plate}`;
      for (let v = 2; taken.has(name); v += 1) name = `${stem}_${job.frame}_${V.plate}_v${v}`;
      const { data } = await api.post("/references/from-generation", { generation_id: g.id, index: i, name: `${name}.png` });
      onUploaded?.(data);
      plates.addRef(data);
      patch((s) => ({ plate_edits: { ...(s.plate_edits || {}), [data.id]: job.edit || (s.kf_edits || {})[job.frame] || "" }, negative: V.hidden ? s.negative : CP_NEGATIVE,
        image_jobs: (s.image_jobs || []).map((x) => (x.id === job.id ? { ...x, used: [...(x.used || []), i] } : x)) }));
      toast.success(`${V.Noun} attached — video prompt is ready`);
    } catch (e) { fl("plates", errMsg(e, `Could not attach the ${V.noun}`)); }
  };
  const dismissJob = (j) => patch((s) => ({ image_jobs: (s.image_jobs || []).filter((x) => x.id !== j.id) }));

  const startAlpha = async (g) => { mask.setItems([]); await setMask(null, g.id); onChanged?.(); };

  const limit = limits[modelId];
  const reason = !srcId ? "Upload the source video" : source.uploading || plates.uploading || mask.uploading ? "Waiting for upload…"
    : info && secs > 30.05 ? "Max 30 s — trim the source first"
    : !info ? "Reading video…" : farKf ? `Keyframe #${farKf.frame} is beyond the new clip (frames 0–${total - 1}) — remove it or replace the footage` : !model?.available ? "This model isn't available yet"
    : !V.steps && secs > limit + 0.05 ? `${model.name} takes source clips up to ${limit} s — trim the clip or pick another model`
    : !V.steps && short && !fill ? "Choose Loop or Freeze last frame" : st?.alpha_job ? "Waiting for the AlphaGen mask…"
    : !plates.ids.length && !V.optional ? `Add at least one ${V.noun}` : unassigned ? `Pick the keyframe for @${tagOf(unassigned)}` : missingPlate ? `Describe the change for @${tagOf(missingPlate)}` : !vtemplate.trim() ? "Add the instruction template" : V.steps ? groups.err : "";
  const INFO = ["Upload the source video", "Waiting for upload…", "Reading video…", "Waiting for the AlphaGen mask…"];
  const issue = !reason || INFO.includes(reason) ? null
    : /trim the source|takes source clips/.test(reason) ? "source" : /Loop or Freeze|whole clip in one go|isn't available/.test(reason) ? "models"
    : /beyond the new clip/.test(reason) ? "keyframes" : /keyframe for|clean plate|action plate|Describe the change|Plates at frames/.test(reason) ? "plates" : "generate";
  const alertAt = (at) => (flash?.at === at ? flash.msg : issue === at ? reason : "");

  const runParts = async (parts) => {
    if (!model?.available) { fl("models", "Pick an available video model first"); return; }
    if (!vtemplate.trim()) { fl("generate", "Add the instruction template"); return; }
    setBusy(true);
    let ok = 0;
    for (const p of parts) {
      const pl = platesFor(plateList, p);
      if (!pl.length) { fl("generate", `Part ${p.index} has no ${V.noun} yet — skipped`); continue; }
      const miss = V.optional ? null : pl.find((x) => !(plateEdits[x.id] || "").trim());
      if (miss) { fl("generate", `Part ${p.index}: describe the change for @${tagOf(miss)}`); continue; }
      const sp = (p.end - p.start + 1) / info.fps;
      if (limit && sp > limit + 0.05) { fl("generate", `Part ${p.index} is ${sp.toFixed(1)} s — ${model.name} takes up to ${limit} s; pick another model`); continue; }
      const shortP = sp < dmin - 0.01;
      const { mask_id: _m, fill: _f, fill_target: _t, ...base } = settings;
      const ps = { ...base, duration: Math.min(dmax, Math.max(dmin, Math.ceil(sp - 0.001))), segment: p.index, ...(shortP ? { fill: fill || "freeze", fill_target: dmin } : {}) };
      const pp = [vtemplate.trim(), ...pl.map(plateLine), vnotes.trim()].filter(Boolean).join("\n");
      if (await generate({ model_id: modelId, section: "blackfx", mode: "create", prompt: pp, settings: ps, image_ids: pl.map((x) => x.id), video_id: p.ref.id }, (m) => fl("generate", `Part ${p.index}: ${m}`))) ok += 1;
    }
    setBusy(false);
    if (ok) toast.success(`${ok} part${ok > 1 ? "s" : ""} queued — they run one after another`);
  };

  const goAction = async () => {
    setBusy(true);
    try {
      await api.post("/bfx/action/generate", { project_id: projectId, source_id: srcId, model_id: modelId, notes: vnotes.trim(), trim,
        settings: { resolution, aspect_ratio: settings.aspect_ratio, negative_prompt: negative, ...(maskId ? { mask_id: maskId } : {}) },
        anchors });
      toast.success("BFX Action started — the whole clip is generated in one continuous take");
      onChanged?.();
    } catch (e) { fl("generate", errMsg(e, "Could not start BFX Action")); } finally { setBusy(false); }
  };

  const go = async () => {
    if (V.steps) return goAction();
    setBusy(true);
    const outside = trim ? plateList.filter((p) => { const f = plateFrame(p); return f != null && (f < trim[0] || f > trim[1]); }) : [];
    const keep = plateList.filter((p) => !outside.includes(p));
    const pr = outside.length ? prompt.split("\n").filter((l) => !outside.some((p) => l.startsWith(`@${tagOf(p)} —`))).join("\n") : prompt;
    await generate({ model_id: modelId, section: "blackfx", mode: "create", prompt: pr, settings, image_ids: keep.map((p) => p.id), video_id: srcId }, (m) => fl("generate", m));
    setBusy(false);
  };

  if (!st) return <div className="flex-1 flex items-center justify-center text-zinc-500 text-sm bfx" data-testid="cp-loading"><Loader2 className="w-4 h-4 animate-spin mr-2" />Restoring your {V.steps ? "BFX Action" : "BFX Cleanup"} session…</div>;

  return (
    <div className="flex-1 flex min-h-0 bfx" data-testid="bfx-section" data-variant={variant}>
      <aside className="w-[400px] shrink-0 border-r border-[#1065c0]/40 bg-[#05080E]/75 backdrop-blur-xl flex flex-col min-h-0" data-testid="bfx-sidebar">
        {brand}
        <div className="flex-1 overflow-y-auto p-4 space-y-5" data-testid="cp-panel">
          <div className="flex justify-end -mb-3">{resetButton}</div>
          {toolCard}
          <Wrap on={V.steps} n={1} title="Load Footage" testid="act-step-footage" done={!!(srcId && info)}>
          <UploadZone testid="cp-source" title={V.steps ? "Source footage" : "Source video"} badge="MP4 / MOV up to 500 MB" subtitle="We read every frame so you can mark the areas to clean"
            accept="video/mp4,video/quicktime,video/x-matroska" max={1} items={source.items} onFiles={source.add} onRemove={source.remove} />
          {info && <div className="text-[10px] font-mono text-zinc-400 -mt-3" data-testid="cp-source-info">{info.width}×{info.height} · {info.fps} fps · <span className="text-white">{info.frames} frames (0–{info.frames - 1})</span></div>}
          {srcId && <div className="flex justify-end -mt-2"><ReplaceSource source={source} accept="video/mp4,video/quicktime,video/x-matroska" testid="cp-replace-source" /></div>}
          <InlineAlert msg={alertAt("source")} testid="cp-alert-source" />
          {srcId && info && <VirtualTrim key={srcId} total={info.frames} fps={info.fps} value={trim} onChange={(t) => patch({ trim: t })} />}
          {!srcId && <SequenceImport projectId={projectId} onUploaded={onUploaded} slot={`${V.slot}-seq`} testid="cp-seq"
            onReady={(ref) => { onUploaded?.(ref); source.setItems([ref]); }} onError={(m) => fl("source", m)} />}
          {srcId && info && !V.steps && <PartsPanel projectId={projectId} src={src} info={info} st={st} patch={patch} plates={plateList} onRun={runParts} running={busy} minSecs={dmin} />}
          </Wrap>
          <Wrap on={V.steps} n={2} title="Keyframes" testid="act-step-keyframes" done={keyframes.length > 0}>
          {srcId ? <KeyframeList keyframes={keyframes} onRemove={removeKeyframe} trim={trim} /> : V.steps && <p className="text-[11px] text-zinc-500">Load footage first, then mark keyframes in the viewer.</p>}
          <InlineAlert msg={alertAt("keyframes")} testid="cp-alert-keyframes" />
          </Wrap>
          <Wrap on={V.steps} n={3} title="Action Plates" testid="act-step-plates" done={plateList.length > 0 && !unassigned}>
          {srcId && (
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={attachZip} disabled={!keyframes.length || zipping} data-testid="cp-attach-clean-plate"
                className="h-10 rounded-lg border border-[#1477d2]/60 text-sm text-white flex items-center justify-center gap-1.5 hover:bg-[#0747a1]/30 disabled:opacity-40">
                <Download className="w-4 h-4" />{zipping ? "Preparing…" : `Attach ${V.Noun}`}</button>
              <button type="button" onClick={() => setDlg(true)} disabled={!keyframes.length} data-testid="cp-create-clean-plate"
                className="h-10 rounded-lg bfx-gradient-bg text-sm font-semibold text-white flex items-center justify-center gap-1.5 disabled:opacity-40">
                <Wand2 className="w-4 h-4" />Create {V.Noun}</button>
            </div>
          )}
          <Candidates jobs={st.image_jobs} onUse={useIt} onDismiss={dismissJob} />
          {keyframes.length
            ? <UploadZone testid="cp-plates" title={`${V.Noun}s`} badge="PNG / JPEG · up to 10" subtitle="Made outside from a downloaded keyframe — then pick which keyframe it belongs to"
                accept="image/png,image/jpeg,image/webp" max={10} items={plates.items} onFiles={plates.add} onRemove={plates.remove}
                onSelect={(i) => setSelPlate(plates.items[i]?.id || null)} selected={plates.items.findIndex((x) => x.id === selPlate)}>
                {selP?.id && (
                  <div className="flex items-center gap-2 pt-1" data-testid="cp-plate-assign">
                    <span className="text-[11px] text-zinc-300 truncate">@{tagOf(selP)} is for</span>
                    <select value={plateFrame(selP) ?? ""} onChange={(e) => assignPlate(selP, Number(e.target.value))} data-testid={`cp-plate-frame-${selP.id}`}
                      className={`ax-input bfx-input !h-7 !py-0 text-[11px] w-32 ${plateFrame(selP) == null ? "!border-amber-400/70" : ""}`} title={`Which keyframe this ${V.noun} is for`}>
                      <option value="" disabled>Pick keyframe…</option>
                      {keyframes.map((k) => <option key={k.frame} value={k.frame}>Frame #{k.frame}</option>)}
                    </select>
                  </div>
                )}
                {!selP && unassigned && <p className="text-[11px] text-amber-300 pt-1" data-testid="cp-plate-assign-hint">Click a {V.noun} thumbnail to choose its keyframe</p>}
              </UploadZone>
            : <div className="rounded-xl border border-dashed border-zinc-700 p-3 text-[11px] text-zinc-400" data-testid="cp-plates-locked">{V.optional ? `Optional — mark a keyframe first if you want ${V.noun}s; with none, your prompt drives the whole clip.` : `Mark at least one keyframe first — ${V.noun}s are matched to a keyframe frame.`}</div>}
          <InlineAlert msg={alertAt("plates")} testid="cp-alert-plates" />
          </Wrap>
          <Wrap on={V.steps} n={4} title="Alpha Import + Generate" testid="act-step-generate" done={false}>
          <label className="flex items-center justify-between gap-3 rounded-lg border border-zinc-800 p-2.5 cursor-pointer" data-testid="cp-bg-only">
            <span><span className="block text-xs text-white font-semibold">BG Only</span><span className="block text-[10px] text-zinc-400">Remove the character / subject — keep camera, background and lighting the same</span></span>
            <Switch checked={bgOnly} onCheckedChange={(v) => patch({ bg_only: v })} data-testid="cp-bg-only-switch" />
          </label>
          <div>
            <UploadZone testid="cp-mask" title={V.steps ? "Luma Mask" : <>Upload Luma Mask <span className="text-zinc-500">(optional · MP4 with alpha)</span></>} badge={V.steps ? "white = generate · black = source" : "white = no change · black = change"}
              subtitle={V.steps ? "Optional · White = the action is generated here; black = your source, motion matched exactly" : "White areas stay exactly as your source; only the black areas change"} accept="video/mp4,video/quicktime,video/webm"
              max={1} items={mask.items} onFiles={mask.add} onRemove={mask.remove} disabled={bgOnly} />
            {bgOnly && <p className="text-[10px] text-amber-300 mt-1" data-testid="cp-mask-disabled">Luma Mask is off while BG Only is on.</p>}
            {st.alpha_job
              ? <div className="mt-2 text-xs text-[#64b7f6] flex items-center gap-1.5" data-testid="cp-alphagen-running"><Loader2 className="w-3.5 h-3.5 animate-spin" />AlphaGen is creating your mask in the background…</div>
              : <div className="mt-2 flex items-center gap-4 flex-wrap">
                  <button type="button" onClick={() => setAlphaDlg(true)} disabled={!srcId || !info} data-testid="cp-use-alphagen" className="text-xs text-[#64b7f6] hover:text-white flex items-center gap-1.5 disabled:opacity-40"><Layers className="w-3.5 h-3.5" />Use AlphaGen to create mask</button>
                  <MaskHistoryPicker items={items} onPick={async (ref) => { mask.setItems([ref]); serverMask.current = ref.id; await setMask(ref); }} />
                </div>}
          </div>
          <div data-testid="cp-video-prompt">
            <Label>Video prompt</Label>
            {!V.hidden && <details className="rounded-lg border border-zinc-800 p-2 mb-2" data-testid="cp-video-template">
              <summary className="text-[11px] text-zinc-300 cursor-pointer">Instruction template <span className="text-zinc-500">(keeps motion, light & length)</span></summary>
              <div className="mt-2"><PromptBox value={vtemplate} onChange={(v) => patch({ vtemplate: v })} testid="cp-video-template-box" minRows={3} className="ax-input bfx-input text-xs" /></div>
            </details>}
            {!plateList.length && <p className="text-[11px] text-zinc-500 mb-2">Add {V.noun}s — each gets its own line here{V.optional ? " (optional)" : ""}.</p>}
            <div className="space-y-2">
              {plateList.map((p) => (
                <div key={p.id} className="rounded-lg border border-zinc-800 bg-[#0B0F16] p-2" data-testid={`cp-plate-prompt-${p.id}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <img src={mediaUrl(p.url)} alt="" className="h-8 w-12 object-cover rounded" />
                    <span className="ax-badge !text-[#64b7f6] !normal-case truncate" data-testid={`cp-plate-tag-${p.id}`}>@{tagOf(p)}</span>
                  </div>
                  {V.steps && <ModeToggle id={p.id} mode={plateModes[p.id] || "exact"} onChange={(m) => setMode(p.id, m)} />}
                  <PromptBox value={plateEdits[p.id] || ""} onChange={(v) => patch((s) => ({ plate_edits: { ...(s.plate_edits || {}), [p.id]: v } }))}
                    testid={`cp-plate-edit-${p.id}`} minRows={2} danger={V.steps} placeholder={V.optional ? "Optional — the action and its timing, e.g. 3 rounds hit the wall from frame 44, sparks peak at frame 47, debris settles by frame 56" : "What this clean plate changes in the shot"} className="ax-input bfx-input text-xs" />
                </div>
              ))}
            </div>
            <div className="mt-2"><PromptBox value={vnotes} onChange={(v) => patch({ vnotes: v })} testid="cp-video-notes" minRows={1} danger={V.steps} tags={plateList.map((p) => ({ tag: tagOf(p), ref: p }))} placeholder={`Extra notes for the whole shot (optional) · type @ to tag a ${V.noun}`} className="ax-input bfx-input text-xs" /></div>
            {!V.hidden && <details className="mt-2" data-testid="cp-final-prompt"><summary className="text-[11px] text-[#64b7f6] cursor-pointer">Preview final prompt</summary>
              <pre className="mt-1 whitespace-pre-wrap text-[10px] text-zinc-400 font-mono bg-black/40 rounded p-2" data-testid="cp-final-prompt-text">{prompt}</pre></details>}
          </div>
          {!V.hidden && <div><Label>Negative prompt</Label>
            <PromptBox value={negBase} onChange={(v) => patch({ negative: v })} testid="cp-negative-prompt" minRows={2} className="ax-input bfx-input text-xs" />
          </div>}
          <p className="text-[11px] text-zinc-500 leading-relaxed flex gap-2" data-testid="cp-fps-note"><Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-[#41a7f5]" /><span>
            {info ? <>Output is matched to your source: <b className="text-zinc-300">{info.frames} frames @ {info.fps} fps</b>. </> : null}{V.steps ? "The whole clip is generated in one continuous take — no cuts, splices or fades. Exact Match plates lock their frame; Reference plates only guide; consequences carry forward between plates. Frames count from 0. " : ""}Export MP4 (original fps) or a PNG / TGA image sequence from the result — every frame is kept.</span></p>
          <div>
            <Label>Video model</Label>
            <div className="grid grid-cols-2 gap-1.5" data-testid="cp-video-models">
              {vmodels.map((m) => (
                <button key={m.id} type="button" onClick={() => patch({ model_id: m.id })} disabled={!m.available} data-testid={`cp-video-model-${m.id}`}
                  className={`text-left p-2 rounded-lg border transition-colors disabled:opacity-40 ${m.id === modelId ? "border-[#1e97f3] bg-[#0747a1]/30" : "border-zinc-800 hover:border-[#1477d2]/60"}`}>
                  <div className="text-xs font-semibold text-white">{m.name}</div>
                  <div className="text-[10px] font-mono text-zinc-500" data-testid={`cp-video-model-limits-${m.id}`}>{V.steps ? `${m.id === "gemini-omni-flash-1.1" ? "true edit" : "follows source"} · clip ≤ ${limits[m.id]} s` : `min ${minOf(m)} s · source ≤ ${limits[m.id]} s`}</div>
                </button>
              ))}
            </div>
          </div>
          <InlineAlert msg={alertAt("models")} testid="cp-alert-models" />
          {short && <FillPicker value={fill} onChange={(v) => patch({ fill: v })} secs={secs} min={dmin} />}
          {!!res.length && (
            <div><Label hint={`· default matches your ${info ? `${info.width}×${info.height}` : ""} source · max ${maxRes(model) || res[res.length - 1]}`}>Resolution</Label>
              <div className="flex flex-wrap gap-1.5" data-testid="cp-resolution">{res.map((r) => <button key={r} type="button" onClick={() => patch({ resolution: r })} data-testid={`cp-resolution-${r}`}
                className={`ax-chip ${resolution === r ? "!border-[#1e97f3] text-white" : ""}`}>{r}</button>)}</div>
            </div>
          )}
          </Wrap>
        </div>
        <div className="p-4 border-t border-[#1065c0]/40">
          <InlineAlert msg={alertAt("generate")} testid="cp-alert-generate" />
          <GenerateButton usd={usd} onClick={go} busy={busy} disabledReason={reason} testid="cp-generate" confirm className="w-full bfx-generate mt-2" />
          {reason && !issue && <p className="text-[11px] text-zinc-500 text-center mt-2" data-testid="cp-generate-hint">{reason}</p>}
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto p-6 bg-[#070A10]/40" data-testid="bfx-history">
        {srcId && info && <FrameEditor projectId={projectId} source={src} info={info} keyframes={keyframes} onSave={saveKeyframe} />}
        {header}
        <GenerationGrid items={items} onOpen={onOpen} onRetry={onRetry} onChanged={onChanged} cols="grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3"
          emptyTitle="No Black FX results yet" emptyHint={V.empty} />
      </main>
      <CleanPlateDialog variant={V.dialog} open={dlg} onOpenChange={setDlg} models={models} info={info} projectId={projectId} keyframes={keyframes} st={st} patch={patch} onUse={useIt} onStarted={onChanged} onUploaded={onUploaded} />
      {srcId && <AlphaGenDialog open={alphaDlg} onOpenChange={setAlphaDlg} projectId={projectId} source={src} info={info} onStarted={startAlpha} />}
    </div>
  );
}
